import React, { useCallback, useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import RefreshIcon from "@material-ui/icons/Refresh";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../../services/api";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1, 0, 2)
  },
  viewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column"
    }
  },
  viewTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: 1.2
  },
  viewSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: 4
  },
  refreshButton: {
    flexShrink: 0
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: "100%"
    }
  },
  sectionButton: {
    [theme.breakpoints.down("xs")]: {
      flex: 1
    }
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(160px, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, minmax(160px, 1fr))"
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(140px, 1fr))"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  metricCard: {
    position: "relative",
    minHeight: 104,
    padding: theme.spacing(1.75, 2),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    boxShadow: "none",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      width: 4,
      backgroundColor: props => props.accent
    }
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.3
  },
  metricValue: {
    color: theme.palette.text.primary,
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.2,
    marginTop: theme.spacing(0.75)
  },
  metricDetail: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginTop: 4
  },
  capacityProgress: {
    margin: theme.spacing(2, 0, 3)
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 7
  },
  progressTrack: {
    height: 10,
    overflow: "hidden",
    borderRadius: 999,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.10)"
        : "#E7ECF4"
  },
  progressFill: {
    height: "100%",
    borderRadius: "inherit",
    transition: "width 300ms ease"
  },
  queuesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  queuesGridSingle: {
    gridTemplateColumns: "minmax(0, 1fr)"
  },
  queueCard: {
    display: "flex",
    minHeight: 286,
    flexDirection: "column",
    padding: theme.spacing(2.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    boxShadow: "none"
  },
  queueHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5)
  },
  queueTitle: {
    fontSize: 17,
    fontWeight: 700
  },
  queueBadge: {
    padding: "6px 10px",
    borderRadius: 999,
    color: props => props.accent,
    backgroundColor: props =>
      theme.palette.type === "dark"
        ? `${props.accent}24`
        : `${props.accent}14`,
    fontSize: 11,
    fontWeight: 600
  },
  queueMetrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(2.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    }
  },
  queueMetricsExpanded: {
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    }
  },
  queueMetricLabel: {
    minHeight: 30,
    color: theme.palette.text.secondary,
    fontSize: 11,
    lineHeight: 1.35
  },
  queueMetricValue: {
    color: theme.palette.text.primary,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.2
  },
  queueProgress: {
    marginTop: "auto",
    paddingTop: theme.spacing(2.25),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  queueProgressDetail: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginBottom: 7
  },
  error: {
    padding: theme.spacing(3),
    textAlign: "center",
    border: `1px solid ${theme.palette.error.light}`,
    boxShadow: "none"
  },
  errorTitle: {
    fontWeight: 600,
    marginBottom: 6
  },
  errorDetail: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginBottom: theme.spacing(2)
  },
  skeletonCard: {
    height: 104,
    borderRadius: 10
  },
  emptyQueues: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    boxShadow: "none"
  }
}));

const VISIBLE_QUEUES_STORAGE_KEY = "dashboard.operational-day.visible-queues";
const DEFAULT_VISIBLE_QUEUES = { n1: true, n2: true };

const loadVisibleQueues = () => {
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(VISIBLE_QUEUES_STORAGE_KEY)
    );

    return {
      n1: typeof saved?.n1 === "boolean" ? saved.n1 : true,
      n2: typeof saved?.n2 === "boolean" ? saved.n2 : true
    };
  } catch (error) {
    return DEFAULT_VISIBLE_QUEUES;
  }
};

const clampPercentage = value => Math.max(0, Math.min(Number(value) || 0, 100));

const formatMinutes = value => {
  const minutes = Number(value) || 0;
  const sign = minutes < 0 ? "-" : "";
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const remainingMinutes = absolute % 60;

  if (!hours) {
    return `${sign}${remainingMinutes}min`;
  }

  return `${sign}${hours}h${
    remainingMinutes ? String(remainingMinutes).padStart(2, "0") : ""
  }`;
};

const formatGeneratedAt = value => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });
};

const MetricCard = ({ accent, label, value, detail }) => {
  const classes = useStyles({ accent });

  return (
    <Paper className={classes.metricCard}>
      <div className={classes.metricLabel}>{label}</div>
      <div className={classes.metricValue}>{value}</div>
      <div className={classes.metricDetail}>{detail}</div>
    </Paper>
  );
};

const ProgressBar = ({ classes, color, percentage }) => (
  <div className={classes.progressTrack}>
    <div
      className={classes.progressFill}
      style={{
        backgroundColor: color,
        width: `${clampPercentage(percentage)}%`
      }}
    />
  </div>
);

const QueuePanel = ({
  accent,
  title,
  badge,
  metrics,
  progressLabel,
  progressDetail,
  percentage,
  expanded = false
}) => {
  const classes = useStyles({ accent });

  return (
    <Paper className={classes.queueCard}>
      <div className={classes.queueHeader}>
        <Typography className={classes.queueTitle}>{title}</Typography>
        <div className={classes.queueBadge}>{badge}</div>
      </div>

      <div
        className={`${classes.queueMetrics} ${
          expanded ? classes.queueMetricsExpanded : ""
        }`}
      >
        {metrics.map(metric => (
          <div key={metric.label}>
            <div className={classes.queueMetricLabel}>{metric.label}</div>
            <div className={classes.queueMetricValue}>{metric.value}</div>
          </div>
        ))}
      </div>

      <div className={classes.queueProgress}>
        <div className={classes.queueProgressDetail}>
          <span>{progressLabel}</span>
          <span>{progressDetail || `${percentage}%`}</span>
        </div>
        <ProgressBar
          classes={classes}
          color={accent}
          percentage={percentage}
        />
      </div>
    </Paper>
  );
};

const LoadingState = ({ visibleQueues }) => {
  const classes = useStyles({ accent: "#2563EB" });
  const visibleCount = Object.values(visibleQueues).filter(Boolean).length;

  return (
    <>
      <div className={classes.cardsGrid}>
        {[0, 1, 2, 3, 4].map(item => (
          <Skeleton
            key={item}
            variant="rect"
            className={classes.skeletonCard}
          />
        ))}
      </div>
      <Skeleton height={52} />
      {visibleCount > 0 && (
        <div
          className={`${classes.queuesGrid} ${
            visibleCount === 1 ? classes.queuesGridSingle : ""
          }`}
        >
          {visibleQueues.n1 && <Skeleton variant="rect" height={286} />}
          {visibleQueues.n2 && <Skeleton variant="rect" height={286} />}
        </div>
      )}
    </>
  );
};

const OperationalDayView = () => {
  const classes = useStyles({ accent: "#2563EB" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sectionsAnchor, setSectionsAnchor] = useState(null);
  const [visibleQueues, setVisibleQueues] = useState(loadVisibleQueues);

  const loadData = useCallback(async (refresh = false) => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/dashboard/operational-day", {
        params: refresh ? { refresh: 1 } : undefined
      });
      setData(response.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Não foi possível consultar os indicadores."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    window.localStorage.setItem(
      VISIBLE_QUEUES_STORAGE_KEY,
      JSON.stringify(visibleQueues)
    );
  }, [visibleQueues]);

  const toggleQueue = queue => {
    setVisibleQueues(current => ({
      ...current,
      [queue]: !current[queue]
    }));
  };

  const capacity = data?.capacity;
  const n1 = data?.n1;
  const n2 = data?.n2;
  const visibleQueueCount = Object.values(visibleQueues).filter(Boolean).length;

  return (
    <div className={classes.root}>
      <div className={classes.viewHeader}>
        <div>
          <Typography variant="h5" className={classes.viewTitle}>
            Visão operacional do dia
          </Typography>
          <div className={classes.viewSubtitle}>
            Capacidade, filas N1/N2 e riscos operacionais
            {data?.generated_at &&
              ` · atualizado às ${formatGeneratedAt(data.generated_at)}`}
          </div>
        </div>

        <div className={classes.headerActions}>
          <Button
            className={classes.sectionButton}
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<ViewColumnOutlinedIcon />}
            onClick={event => setSectionsAnchor(event.currentTarget)}
          >
            Seções
          </Button>
          <Menu
            anchorEl={sectionsAnchor}
            keepMounted
            open={Boolean(sectionsAnchor)}
            onClose={() => setSectionsAnchor(null)}
          >
            <MenuItem onClick={() => toggleQueue("n1")}>
              <Checkbox
                edge="start"
                color="primary"
                checked={visibleQueues.n1}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary="Fila N1" />
            </MenuItem>
            <MenuItem onClick={() => toggleQueue("n2")}>
              <Checkbox
                edge="start"
                color="primary"
                checked={visibleQueues.n2}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary="Fila N2" />
            </MenuItem>
          </Menu>
          <Button
            className={classes.refreshButton}
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => loadData(true)}
            disabled={loading}
          >
            Reconsultar
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <LoadingState visibleQueues={visibleQueues} />
      ) : error && !data ? (
        <Paper className={classes.error}>
          <div className={classes.errorTitle}>Indicadores indisponíveis</div>
          <div className={classes.errorDetail}>{error}</div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => loadData(true)}
          >
            Tentar novamente
          </Button>
        </Paper>
      ) : (
        <>
          <div className={classes.cardsGrid}>
            <MetricCard
              accent="#2563EB"
              label="Capacidade total"
              value={formatMinutes(capacity.total_minutes)}
              detail="Equipe disponível hoje"
            />
            <MetricCard
              accent="#16A36A"
              label="Capacidade planejável"
              value={formatMinutes(capacity.planned_minutes)}
              detail="85% da capacidade total"
            />
            <MetricCard
              accent="#7451D8"
              label="Horas utilizadas"
              value={formatMinutes(capacity.used_minutes)}
              detail={`${capacity.used_percentage}% da planejada`}
            />
            <MetricCard
              accent="#F08A0A"
              label="Horas restantes"
              value={formatMinutes(capacity.remaining_minutes)}
              detail={
                capacity.remaining_minutes < 0
                  ? "Acima da capacidade"
                  : "Disponíveis hoje"
              }
            />
            <MetricCard
              accent="#2563EB"
              label="Equipe disponível"
              value={capacity.team_available}
              detail={`${capacity.team_available} ${
                capacity.team_available === 1 ? "pessoa ativa" : "pessoas ativas"
              } hoje`}
            />
          </div>

          <div className={classes.capacityProgress}>
            <div className={classes.progressHeader}>
              <span>Utilização da capacidade planejada</span>
              <span>{capacity.used_percentage}%</span>
            </div>
            <ProgressBar
              classes={classes}
              color="#2563EB"
              percentage={capacity.used_percentage}
            />
          </div>

          {visibleQueueCount > 0 ? (
            <div
              className={`${classes.queuesGrid} ${
                visibleQueueCount === 1 ? classes.queuesGridSingle : ""
              }`}
            >
              {visibleQueues.n1 && (
                <QueuePanel
                  accent="#2563EB"
                  title="Fila N1"
                  badge="Atendimento e triagem"
                  expanded={visibleQueueCount === 1}
                  metrics={[
                    { label: "Novos", value: n1.new },
                    { label: "Em atendimento", value: n1.in_service },
                    {
                      label: "Aguardando cliente",
                      value: n1.waiting_customer
                    },
                    { label: "Encaminhados ao N2", value: n1.forwarded_n2 },
                    { label: "Concluídos", value: n1.completed },
                    {
                      label: "1ª resposta média",
                      value: `${n1.first_response_minutes} min`
                    }
                  ]}
                  progressLabel="Resolvidos diretamente no N1"
                  percentage={n1.direct_resolution_percentage}
                />
              )}

              {visibleQueues.n2 && (
                <QueuePanel
                  accent="#7451D8"
                  title="Fila N2"
                  badge="Análise técnica"
                  expanded={visibleQueueCount === 1}
                  metrics={[
                    { label: "Novos", value: n2.new },
                    { label: "Em análise", value: n2.in_analysis },
                    {
                      label: "Aguardando informações",
                      value: n2.waiting_information
                    },
                    {
                      label: "Aguardando desenvolvimento",
                      value: n2.waiting_development
                    },
                    {
                      label: "Disponíveis para teste",
                      value: n2.available_testing
                    },
                    { label: "Resolvidos", value: n2.resolved }
                  ]}
                  progressLabel="Chamados movimentados nas últimas 24h"
                  progressDetail={`${n2.moved_24h} de ${n2.active} · ${n2.moved_percentage}%`}
                  percentage={n2.moved_percentage}
                />
              )}
            </div>
          ) : (
            <Paper className={classes.emptyQueues}>
              Nenhuma fila selecionada. Use o menu “Seções” para exibir N1 ou N2.
            </Paper>
          )}
        </>
      )}
    </div>
  );
};

export default OperationalDayView;
